import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { Settings as SettingsIcon, ShoppingBag, Server, Globe, Save, TestTube, CheckCircle, XCircle, Loader2, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { Button, Badge } from '../shared';

interface IntegrationSettings {
  userId: string;
  shopify?: {
    storeUrl: string;
    apiToken: string;
    enabled: boolean;
    lastTested?: Date;
    status?: 'connected' | 'disconnected' | 'error';
  };
  sftp?: {
    host: string;
    port: number;
    username: string;
    password: string;
    remotePath: string;
    enabled: boolean;
    lastTested?: Date;
    status?: 'connected' | 'disconnected' | 'error';
  };
  cms?: {
    type: 'wordpress' | 'contentful' | 'webflow' | 'custom';
    apiUrl: string;
    apiKey: string;
    enabled: boolean;
  };
  updatedAt: Timestamp;
}

export function Settings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'shopify' | 'sftp' | 'cms'>('shopify');

  // Shopify settings
  const [shopifyStoreUrl, setShopifyStoreUrl] = useState('');
  const [shopifyApiToken, setShopifyApiToken] = useState('');
  const [shopifyEnabled, setShopifyEnabled] = useState(false);
  const [shopifyStatus, setShopifyStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
  const [shopifyTesting, setShopifyTesting] = useState(false);
  const [shopifySyncing, setShopifySyncing] = useState(false);

  // SFTP settings
  const [sftpHost, setSftpHost] = useState('');
  const [sftpPort, setSftpPort] = useState(22);
  const [sftpUsername, setSftpUsername] = useState('');
  const [sftpPassword, setSftpPassword] = useState('');
  const [sftpRemotePath, setSftpRemotePath] = useState('/');
  const [sftpEnabled, setSftpEnabled] = useState(false);
  const [sftpStatus, setSftpStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
  const [sftpTesting, setSftpTesting] = useState(false);
  const [sftpSyncing, setSftpSyncing] = useState(false);

  // UI state
  const [showShopifyToken, setShowShopifyToken] = useState(false);
  const [showSftpPassword, setShowSftpPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;

    try {
      const docRef = doc(db, 'integrationSettings', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as IntegrationSettings;

        // Load Shopify settings
        if (data.shopify) {
          setShopifyStoreUrl(data.shopify.storeUrl || '');
          setShopifyApiToken(data.shopify.apiToken || '');
          setShopifyEnabled(data.shopify.enabled || false);
          setShopifyStatus(data.shopify.status || 'disconnected');
        }

        // Load SFTP settings
        if (data.sftp) {
          setSftpHost(data.sftp.host || '');
          setSftpPort(data.sftp.port || 22);
          setSftpUsername(data.sftp.username || '');
          setSftpPassword(data.sftp.password || '');
          setSftpRemotePath(data.sftp.remotePath || '/');
          setSftpEnabled(data.sftp.enabled || false);
          setSftpStatus(data.sftp.status || 'disconnected');
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const settings: IntegrationSettings = {
        userId: user.uid,
        shopify: {
          storeUrl: shopifyStoreUrl,
          apiToken: shopifyApiToken,
          enabled: shopifyEnabled,
          status: shopifyStatus,
        },
        sftp: {
          host: sftpHost,
          port: sftpPort,
          username: sftpUsername,
          password: sftpPassword,
          remotePath: sftpRemotePath,
          enabled: sftpEnabled,
          status: sftpStatus,
        },
        updatedAt: Timestamp.now(),
      };

      await setDoc(doc(db, 'integrationSettings', user.uid), settings);
      alert('✅ Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Failed to save settings. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  const testShopifyConnection = async () => {
    setShopifyTesting(true);
    try {
      // TODO: Call Cloud Function to test Shopify connection
      // For now, just validate the inputs
      if (!shopifyStoreUrl || !shopifyApiToken) {
        throw new Error('Store URL and API token are required');
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      setShopifyStatus('connected');
      alert('✅ Shopify connection successful!');
    } catch (error) {
      setShopifyStatus('error');
      alert(`❌ Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setShopifyTesting(false);
    }
  };

  const testSftpConnection = async () => {
    setSftpTesting(true);
    try {
      if (!sftpHost || !sftpUsername || !sftpPassword) {
        throw new Error('Host, username, and password are required');
      }

      // TODO: Call Cloud Function to test SFTP connection
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      setSftpStatus('connected');
      alert('✅ SFTP connection successful!');
    } catch (error) {
      setSftpStatus('error');
      alert(`❌ Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSftpTesting(false);
    }
  };

  const syncShopifyProducts = async () => {
    if (!user || !shopifyStoreUrl || !shopifyApiToken) {
      alert('Please configure Shopify credentials first');
      return;
    }

    setShopifySyncing(true);
    try {
      // Call Shopify sync Cloud Function
      const shopifySyncFn = httpsCallable(functions, 'shopifySyncProducts');
      const result = await shopifySyncFn({ userId: user.uid });
      const data = result.data as any;

      if (data.success && data.products) {
        // Trigger content normalization for each synced product
        const normalizeFn = httpsCallable(functions, 'normalizeContent');
        let normalizedCount = 0;

        for (const product of data.products) {
          try {
            await normalizeFn({
              sourceSystem: 'shopify',
              sourceRecordId: product.id,
            });
            normalizedCount++;
          } catch (error) {
            console.error(`Failed to normalize product ${product.id}:`, error);
          }
        }

        alert(`✅ Synced ${data.products.length} products. Normalized ${normalizedCount} into Content Library.`);
      } else {
        alert('✅ Sync completed but no products returned');
      }
    } catch (error) {
      console.error('Shopify sync error:', error);
      alert(`❌ Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setShopifySyncing(false);
    }
  };

  const syncSftpData = async () => {
    if (!user || !sftpHost || !sftpUsername || !sftpPassword) {
      alert('Please configure SFTP credentials first');
      return;
    }

    setSftpSyncing(true);
    try {
      // Call SFTP sync Cloud Function
      const sftpSyncFn = httpsCallable(functions, 'sftpSyncToFirestore');
      const result = await sftpSyncFn({ userId: user.uid });
      const data = result.data as any;

      if (data.success && data.records) {
        // Trigger content normalization for each synced record
        const normalizeFn = httpsCallable(functions, 'normalizeContent');
        let normalizedCount = 0;

        for (const record of data.records) {
          try {
            await normalizeFn({
              sourceSystem: 'sftp',
              sourceRecordId: record.id,
            });
            normalizedCount++;
          } catch (error) {
            console.error(`Failed to normalize SFTP record ${record.id}:`, error);
          }
        }

        alert(`✅ Synced ${data.records.length} records. Normalized ${normalizedCount} into Content Library.`);
      } else {
        alert('✅ Sync completed but no records returned');
      }
    } catch (error) {
      console.error('SFTP sync error:', error);
      alert(`❌ Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSftpSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-t1-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-display text-gray-900 flex items-center gap-2">
          <SettingsIcon className="w-6 h-6" />
          Integration Settings
        </h1>
        <p className="text-body text-gray-600 mt-1">
          Configure and manage your data source integrations
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-card border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('shopify')}
            className={`flex items-center gap-2 px-6 py-4 text-body font-medium border-b-2 transition-colors ${
              activeTab === 'shopify'
                ? 'border-t1-primary text-t1-primary'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            Shopify
            {shopifyEnabled && (
              <Badge variant={shopifyStatus === 'connected' ? 'success' : shopifyStatus === 'error' ? 'critical' : 'primary'}>
                {shopifyStatus}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('sftp')}
            className={`flex items-center gap-2 px-6 py-4 text-body font-medium border-b-2 transition-colors ${
              activeTab === 'sftp'
                ? 'border-t1-primary text-t1-primary'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Server className="w-4 h-4" />
            SFTP
            {sftpEnabled && (
              <Badge variant={sftpStatus === 'connected' ? 'success' : sftpStatus === 'error' ? 'critical' : 'primary'}>
                {sftpStatus}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('cms')}
            className={`flex items-center gap-2 px-6 py-4 text-body font-medium border-b-2 transition-colors ${
              activeTab === 'cms'
                ? 'border-t1-primary text-t1-primary'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Globe className="w-4 h-4" />
            CMS API
            <Badge variant="warning">Coming Soon</Badge>
          </button>
        </div>

        {/* Shopify Tab */}
        {activeTab === 'shopify' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-heading text-gray-900">Shopify Store Integration</h2>
                <p className="text-caption text-gray-600 mt-1">
                  Connect to your Shopify store to sync products and inventory
                </p>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={shopifyEnabled}
                  onChange={(e) => setShopifyEnabled(e.target.checked)}
                  className="w-4 h-4 text-t1-primary border-gray-300 rounded focus:ring-t1-primary"
                />
                <span className="text-body text-gray-700">Enable Integration</span>
              </label>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-label text-gray-700 mb-1">Store URL</label>
                <input
                  type="text"
                  value={shopifyStoreUrl}
                  onChange={(e) => setShopifyStoreUrl(e.target.value)}
                  placeholder="https://your-store.myshopify.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-chip text-body focus:ring-2 focus:ring-t1-primary focus:border-t1-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-label text-gray-700 mb-1">Admin API Access Token</label>
                <div className="relative">
                  <input
                    type={showShopifyToken ? 'text' : 'password'}
                    value={shopifyApiToken}
                    onChange={(e) => setShopifyApiToken(e.target.value)}
                    placeholder="shpat_..."
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-chip text-body focus:ring-2 focus:ring-t1-primary focus:border-t1-primary outline-none font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowShopifyToken(!showShopifyToken)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showShopifyToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-caption text-gray-500 mt-1">
                  Generate in Shopify Admin → Apps → Develop apps → Create app
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  variant="secondary"
                  onClick={testShopifyConnection}
                  disabled={shopifyTesting || !shopifyStoreUrl || !shopifyApiToken}
                  loading={shopifyTesting}
                >
                  <TestTube className="w-4 h-4" />
                  Test Connection
                </Button>
                <Button
                  variant="primary"
                  onClick={syncShopifyProducts}
                  disabled={shopifySyncing || !shopifyStoreUrl || !shopifyApiToken || shopifyStatus !== 'connected'}
                  loading={shopifySyncing}
                >
                  <RefreshCw className="w-4 h-4" />
                  Sync Products
                </Button>
                {shopifyStatus === 'connected' && (
                  <div className="flex items-center gap-2 text-success-green">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-caption font-medium">Connected</span>
                  </div>
                )}
                {shopifyStatus === 'error' && (
                  <div className="flex items-center gap-2 text-critical-red">
                    <XCircle className="w-4 h-4" />
                    <span className="text-caption font-medium">Connection Failed</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SFTP Tab */}
        {activeTab === 'sftp' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-heading text-gray-900">SFTP File Server</h2>
                <p className="text-caption text-gray-600 mt-1">
                  Connect to SFTP server to sync files and data exports
                </p>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sftpEnabled}
                  onChange={(e) => setSftpEnabled(e.target.checked)}
                  className="w-4 h-4 text-t1-primary border-gray-300 rounded focus:ring-t1-primary"
                />
                <span className="text-body text-gray-700">Enable Integration</span>
              </label>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-label text-gray-700 mb-1">Host</label>
                  <input
                    type="text"
                    value={sftpHost}
                    onChange={(e) => setSftpHost(e.target.value)}
                    placeholder="sftp.example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-chip text-body focus:ring-2 focus:ring-t1-primary focus:border-t1-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-label text-gray-700 mb-1">Port</label>
                  <input
                    type="number"
                    value={sftpPort}
                    onChange={(e) => setSftpPort(parseInt(e.target.value))}
                    placeholder="22"
                    className="w-full px-3 py-2 border border-gray-300 rounded-chip text-body focus:ring-2 focus:ring-t1-primary focus:border-t1-primary outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-label text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    value={sftpUsername}
                    onChange={(e) => setSftpUsername(e.target.value)}
                    placeholder="username"
                    className="w-full px-3 py-2 border border-gray-300 rounded-chip text-body focus:ring-2 focus:ring-t1-primary focus:border-t1-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-label text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showSftpPassword ? 'text' : 'password'}
                      value={sftpPassword}
                      onChange={(e) => setSftpPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-chip text-body focus:ring-2 focus:ring-t1-primary focus:border-t1-primary outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSftpPassword(!showSftpPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSftpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-label text-gray-700 mb-1">Remote Path</label>
                <input
                  type="text"
                  value={sftpRemotePath}
                  onChange={(e) => setSftpRemotePath(e.target.value)}
                  placeholder="/path/to/files"
                  className="w-full px-3 py-2 border border-gray-300 rounded-chip text-body focus:ring-2 focus:ring-t1-primary focus:border-t1-primary outline-none font-mono text-xs"
                />
                <p className="text-caption text-gray-500 mt-1">
                  Directory where Source of Truth files are located
                </p>
              </div>

              <div className="bg-surface-warm rounded-card p-4">
                <h3 className="text-label text-gray-900 mb-2">Supported File Formats</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-caption text-gray-700">
                  <div>• JSON</div>
                  <div>• CSV</div>
                  <div>• XML</div>
                  <div>• YAML</div>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  variant="secondary"
                  onClick={testSftpConnection}
                  disabled={sftpTesting || !sftpHost || !sftpUsername || !sftpPassword}
                  loading={sftpTesting}
                >
                  <TestTube className="w-4 h-4" />
                  Test Connection
                </Button>
                <Button
                  variant="primary"
                  onClick={syncSftpData}
                  disabled={sftpSyncing || !sftpHost || !sftpUsername || !sftpPassword || sftpStatus !== 'connected'}
                  loading={sftpSyncing}
                >
                  <RefreshCw className="w-4 h-4" />
                  Sync Data
                </Button>
                {sftpStatus === 'connected' && (
                  <div className="flex items-center gap-2 text-success-green">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-caption font-medium">Connected</span>
                  </div>
                )}
                {sftpStatus === 'error' && (
                  <div className="flex items-center gap-2 text-critical-red">
                    <XCircle className="w-4 h-4" />
                    <span className="text-caption font-medium">Connection Failed</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CMS Tab */}
        {activeTab === 'cms' && (
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-card p-6 text-center">
              <Globe className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-heading text-blue-900 mb-2">CMS API Integration Coming Soon</h3>
              <p className="text-body text-blue-800 mb-4">
                Connect to WordPress, Contentful, Webflow, and other headless CMS platforms
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-caption text-blue-700">
                <div>WordPress REST API</div>
                <div>Contentful GraphQL</div>
                <div>Webflow API</div>
                <div>Custom Headless CMS</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="bg-white rounded-card border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <p className="text-caption text-gray-600">
            Changes are saved to your account and encrypted at rest
          </p>
          <Button onClick={saveSettings} disabled={saving} loading={saving}>
            <Save className="w-4 h-4" />
            Save All Settings
          </Button>
        </div>
      </div>
    </div>
  );
}
